import type { PaperCreate } from "@/types/paper";

/**
 * Parse a single APA-style reference line into a PaperCreate object.
 *
 * Expected format:
 *   Authors (YYYY). Title. Journal, Volume(Issue), Pages. URL/DOI
 *
 * Anchors on the `(YYYY).` pattern to split the line.
 */
export function parseReferenceLine(line: string): PaperCreate | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  // Anchor on (YYYY). pattern
  const yearMatch = trimmed.match(/\((\d{4})\)\./);
  if (!yearMatch) return null;

  const year = parseInt(yearMatch[1], 10);
  const yearEnd = yearMatch.index! + yearMatch[0].length;

  // Authors: everything before the year parenthetical
  const authorsPart = trimmed.slice(0, yearMatch.index!).trim();
  const authors = parseAuthors(authorsPart);

  // Everything after "(YYYY)."
  const rest = trimmed.slice(yearEnd).trim();

  // Split on periods to get title, journal, etc.
  const segments = rest.split(/\.\s*/);
  if (segments.length < 1 || !segments[0]) return null;

  const title = segments[0].trim();
  if (!title) return null;

  // Journal is the second segment (may contain volume/pages after comma)
  let journal: string | undefined;
  if (segments.length > 1 && segments[1]) {
    const journalParts = segments[1].split(",");
    journal = journalParts[0].trim() || undefined;
  }

  // Extract URL from the full line
  const urlMatch = trimmed.match(/https?:\/\/\S+/);
  const url = urlMatch ? urlMatch[0].replace(/[.,;)]+$/, "") : undefined;

  // Extract DOI
  let doi: string | undefined;
  const doiMatch = trimmed.match(/doi\.org\/([^\s,;)]+)/i);
  if (doiMatch) {
    doi = doiMatch[1].replace(/[.,;)]+$/, "");
  }

  return {
    title,
    authors: authors.length > 0 ? authors : undefined,
    year,
    journal,
    url,
    doi,
  };
}

function parseAuthors(raw: string): string[] {
  if (!raw) return [];

  // Replace " & " and " and " with comma for uniform splitting
  let normalized = raw.replace(/\s*&\s*/g, ", ").replace(/\s+and\s+/g, ", ");
  // Remove trailing commas/whitespace (preserve periods in initials)
  normalized = normalized.replace(/[,\s]+$/, "");

  // Split by comma, then re-join "Last, First" pairs
  const tokens = normalized.split(",").map((t) => t.trim()).filter(Boolean);
  const authors: string[] = [];

  // Heuristic: if a token looks like initials (e.g. "J. K." or "A."),
  // it belongs with the previous token as "Last, Initials"
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const nextToken = tokens[i + 1];
    if (nextToken && /^[A-Z]\.?\s*([A-Z]\.?\s*)*$/.test(nextToken)) {
      authors.push(`${token}, ${nextToken}`);
      i++; // skip the initials token
    } else {
      authors.push(token);
    }
  }

  return authors;
}

export interface ParseResult {
  papers: PaperCreate[];
  skippedLines: string[];
  totalLines: number;
}

/**
 * Parse a multi-line reference file content into PaperCreate objects.
 */
export function parseReferences(content: string): ParseResult {
  const lines = content.split("\n");
  const papers: PaperCreate[] = [];
  const skippedLines: string[] = [];
  let totalLines = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue; // skip blank lines entirely
    totalLines++;

    const paper = parseReferenceLine(trimmed);
    if (paper) {
      papers.push(paper);
    } else {
      skippedLines.push(trimmed);
    }
  }

  return { papers, skippedLines, totalLines };
}
